/**
 * Grail browsing: collection + search pages.
 *
 * <grail-collection> progressively enhances the no-JS filter form rendered by
 * snippets/facets.liquid:
 * - filters/sort apply on change (price inputs debounced) by fetching the
 *   section through the Section Rendering API and swapping [data-results-root]
 * - pills, rarity chips and pagination links load in place
 * - URL kept in sync with history.pushState (back/forward re-render)
 * - result counts announced in a polite live region; focus and open filter
 *   groups survive the swap
 * - the filter drawer becomes a modal dialog (Escape, focus trap, inert page,
 *   focus return); in the theme editor it opens while its section is selected
 * - grid/list choice remembered in localStorage
 */
(() => {
  if (customElements.get('grail-collection')) return;

  const VIEW_KEY = 'grail:collection-view';
  const PRICE_DELAY = 600;
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea, [tabindex]:not([tabindex="-1"])';
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const currentUrl = () => window.location.pathname + window.location.search;

  const storage = {
    get(key) {
      try {
        return window.localStorage.getItem(key);
      } catch (error) {
        return null;
      }
    },
    set(key, value) {
      try {
        window.localStorage.setItem(key, value);
      } catch (error) {
        /* storage unavailable (private mode, blocked): the choice just isn't remembered */
      }
    },
  };

  /** Makes everything outside `element` inert; returns a restore function. */
  function inertOutside(element) {
    const changed = [];
    let node = element;
    while (node && node.parentElement && node !== document.body) {
      for (const sibling of node.parentElement.children) {
        if (sibling === node || sibling.inert || ['SCRIPT', 'STYLE', 'TEMPLATE', 'LINK'].includes(sibling.tagName)) continue;
        sibling.inert = true;
        changed.push(sibling);
      }
      node = node.parentElement;
    }
    return () => changed.forEach((el) => (el.inert = false));
  }

  class GrailCollection extends HTMLElement {
    constructor() {
      super();
      this.cache = new Map();
      this.controller = null;
      this.priceTimer = null;
      this.opener = null;
      this.seq = 0;
      this.pendingUrl = null;
      this.restoreInert = null;
      this.onPopState = this.onPopState.bind(this);
      this.onEditorSelect = this.onEditorSelect.bind(this);
      this.onEditorDeselect = this.onEditorDeselect.bind(this);
      this.onMedia = this.onMedia.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId;
      this.live = this.querySelector('[data-live]');
      this.dataset.enhanced = '';
      this.renderedUrl = currentUrl();
      this.restoreView();
      this.syncOpener();

      this.addEventListener('change', this.onChange);
      this.addEventListener('input', this.onInput);
      this.addEventListener('submit', this.onSubmit);
      this.addEventListener('click', this.onClick);
      this.addEventListener('keydown', this.onKeydown);
      window.addEventListener('popstate', this.onPopState);
      this.media = window.matchMedia('(min-width: 990px)');
      this.media.addEventListener('change', this.onMedia);

      if (window.Shopify?.designMode) {
        document.addEventListener('shopify:section:select', this.onEditorSelect);
        document.addEventListener('shopify:section:deselect', this.onEditorDeselect);
      }
    }

    disconnectedCallback() {
      window.removeEventListener('popstate', this.onPopState);
      this.media?.removeEventListener('change', this.onMedia);
      document.removeEventListener('shopify:section:select', this.onEditorSelect);
      document.removeEventListener('shopify:section:deselect', this.onEditorDeselect);
      this.controller?.abort();
      clearTimeout(this.priceTimer);
      this.restoreInert?.();
      this.restoreInert = null;
    }

    get root() {
      return this.querySelector('[data-results-root]');
    }

    get panel() {
      return this.querySelector('[data-facets-panel]');
    }

    /* ---------------- Events ---------------- */

    onChange(event) {
      const target = event.target;
      if (target.matches('[data-view-input]')) {
        storage.set(VIEW_KEY, target.value);
        return;
      }
      if (target.matches('[data-price-input]')) return; // handled by the debounced input listener
      const form = target.form;
      if (form?.matches('[data-facets-form]')) this.renderFromForm(form);
    }

    onInput(event) {
      const target = event.target;
      if (!target.matches('[data-price-input]') || !target.form) return;
      clearTimeout(this.priceTimer);
      this.priceTimer = setTimeout(() => {
        if (target.checkValidity()) this.renderFromForm(target.form);
      }, PRICE_DELAY);
    }

    onSubmit(event) {
      const form = event.target;
      if (!form.matches('[data-facets-form]')) return; // the card finder submits to /search normally
      event.preventDefault();
      clearTimeout(this.priceTimer);
      if (this.panel?.hasAttribute('data-open')) this.closeDrawer();
      this.renderFromForm(form);
    }

    onClick(event) {
      const opener = event.target.closest('[data-facets-open]');
      if (opener) {
        event.preventDefault();
        this.openDrawer(opener);
        return;
      }

      const closer = event.target.closest('[data-facets-close]');
      if (closer) {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      const link = event.target.closest('a[data-facets-link]');
      if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.button !== 0) return;
      const url = new URL(link.href, window.location.href);
      if (url.origin !== window.location.origin) return;
      event.preventDefault();
      const fromPagination = Boolean(link.closest('.pagination'));
      this.render(url.pathname + url.search, { scrollToResults: fromPagination });
    }

    onKeydown(event) {
      const panel = this.panel;
      if (!panel?.hasAttribute('data-open')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeDrawer();
        return;
      }

      if (event.key === 'Tab') {
        // The backdrop link is tabindex="-1": it must not count as the first stop.
        const items = [...panel.querySelectorAll(FOCUSABLE)].filter((el) => !el.matches('[tabindex="-1"]') && el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    onPopState() {
      // Hash-only navigation (the no-JS #Facets anchor, in-page links) keeps the same results.
      const url = currentUrl();
      if (url === (this.pendingUrl ?? this.renderedUrl)) return;
      this.render(url, { push: false });
    }

    /* Theme editor: show the filter drawer while this section is selected. */
    onEditorSelect(event) {
      if (!event.target.contains(this) || !this.isDrawer()) return;
      this.openDrawer(null, { focus: false });
    }

    onEditorDeselect(event) {
      if (!event.target.contains(this) || !this.panel?.hasAttribute('data-open')) return;
      this.closeDrawer({ restoreFocus: false });
    }

    /* ---------------- Drawer ---------------- */

    /**
     * Widening past the sidebar breakpoint turns an open drawer into the
     * sidebar: without closing it, the page behind would stay inert (and its
     * close button is hidden at desktop), leaving nothing clickable.
     */
    onMedia() {
      requestAnimationFrame(() => {
        if (this.panel?.hasAttribute('data-open') && !this.isDrawer()) this.closeDrawer({ restoreFocus: false });
      });
    }

    /** The panel is a drawer (not the desktop sidebar) while its opener button is shown. */
    isDrawer() {
      const opener = this.querySelector('[data-facets-open]');
      return Boolean(this.panel && opener && opener.getClientRects().length);
    }

    openDrawer(opener, { focus = true } = {}) {
      const panel = this.panel;
      if (!panel) return;
      this.opener = opener || this.querySelector('[data-facets-open]');
      panel.setAttribute('data-open', '');
      panel.setAttribute('role', 'dialog');
      panel.setAttribute('aria-modal', 'true');
      this.opener?.setAttribute('aria-expanded', 'true');
      this.setInert(true);
      if (!focus) return;
      const first = panel.querySelector('.facets-panel__close') || panel;
      first.focus({ preventScroll: true });
    }

    closeDrawer({ restoreFocus = true } = {}) {
      const panel = this.panel;
      if (!panel) return;
      this.setInert(false);
      panel.removeAttribute('data-open');
      panel.removeAttribute('role');
      panel.removeAttribute('aria-modal');
      if (window.location.hash === `#${panel.id}`) {
        history.replaceState(history.state, '', window.location.pathname + window.location.search);
      }
      const opener = this.querySelector('[data-facets-open]');
      opener?.setAttribute('aria-expanded', 'false');
      if (restoreFocus) opener?.focus({ preventScroll: true });
    }

    /** Like the header drawer, an open filter drawer makes the page behind it inert (never the desktop sidebar). */
    setInert(on) {
      this.restoreInert?.();
      this.restoreInert = on && this.isDrawer() ? inertOutside(this.panel) : null;
    }

    syncOpener() {
      const open = Boolean(this.panel?.hasAttribute('data-open'));
      this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', String(open));
    }

    /* ---------------- View ---------------- */

    restoreView() {
      const view = storage.get(VIEW_KEY);
      if (view !== 'grid' && view !== 'list') return;
      const input = this.querySelector(`[data-view-input][value="${view}"]`);
      if (input) input.checked = true;
    }

    /* ---------------- Rendering ---------------- */

    formUrl(form) {
      const params = new URLSearchParams();
      for (const [key, value] of new FormData(form)) {
        if (typeof value === 'string' && value.trim() !== '') params.append(key, value.trim());
      }
      const path = new URL(form.action, window.location.href).pathname;
      const query = params.toString();
      return query ? `${path}?${query}` : path;
    }

    renderFromForm(form) {
      const url = this.formUrl(form);
      // Compare with what is on screen or on its way: a pending request for
      // another URL must still be superseded, even by the URL already shown.
      if (url === (this.pendingUrl ?? this.renderedUrl)) return;
      this.render(url);
    }

    async fetchSection(url, signal) {
      const sectionUrl = `${url}${url.includes('?') ? '&' : '?'}section_id=${encodeURIComponent(this.sectionId)}`;
      if (this.cache.has(sectionUrl)) return this.cache.get(sectionUrl);

      const response = await fetch(sectionUrl, { signal });
      if (!response.ok) throw new Error(`Section request failed: ${response.status}`);
      const html = await response.text();
      this.cache.set(sectionUrl, html);
      return html;
    }

    async render(url, { push = true, scrollToResults = false } = {}) {
      if (!this.root) return;

      // Every render supersedes the previous one, cached or not: abort its
      // request, and drop its response if it still arrives.
      const seq = ++this.seq;
      this.controller?.abort();
      const controller = new AbortController();
      this.controller = controller;
      this.pendingUrl = url;
      this.root.setAttribute('aria-busy', 'true');

      let html;
      try {
        html = await this.fetchSection(url, controller.signal);
      } catch (error) {
        if (error.name === 'AbortError' || seq !== this.seq) return;
        window.location.assign(url); // fall back to a normal page load
        return;
      }
      if (seq !== this.seq) return;
      this.controller = null;
      this.pendingUrl = null;

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const next = doc.querySelector('[data-results-root]');
      if (!next) {
        window.location.assign(url);
        return;
      }

      const root = this.root;
      const state = this.captureState(root);
      this.restoreInert?.();
      this.restoreInert = null;
      root.replaceWith(next);
      this.restoreState(state);

      if (push && url !== currentUrl()) history.pushState({ grail: true }, '', url);
      this.renderedUrl = url;
      this.announce(next.querySelector('[data-results-count]')?.textContent);

      if (scrollToResults) {
        const results = this.querySelector('[data-results]');
        results?.scrollIntoView({ block: 'start', behavior: reduceMotion.matches ? 'auto' : 'smooth' });
        results?.focus({ preventScroll: true });
      }
    }

    captureState(root) {
      const active = document.activeElement;
      const details = {};
      root.querySelectorAll('details[id]').forEach((el) => {
        details[el.id] = el.open;
      });
      const inputs = {};
      root.querySelectorAll('input[type="search"][id]').forEach((el) => {
        inputs[el.id] = el.value;
      });
      let selection = null;
      if (active && root.contains(active) && typeof active.selectionStart === 'number') {
        try {
          selection = [active.selectionStart, active.selectionEnd];
        } catch (error) {
          selection = null;
        }
      }
      return {
        activeId: active && root.contains(active) ? active.id : null,
        hadFocus: Boolean(active && root.contains(active)),
        drawerOpen: Boolean(root.querySelector('[data-facets-panel][data-open]')),
        details,
        inputs,
        selection,
      };
    }

    restoreState(state) {
      const root = this.root;

      Object.entries(state.details).forEach(([id, open]) => {
        const el = root.querySelector(`#${CSS.escape(id)}`);
        if (el) el.open = open;
      });

      Object.entries(state.inputs).forEach(([id, value]) => {
        const el = root.querySelector(`#${CSS.escape(id)}`);
        if (el && el.value !== value) el.value = value;
      });

      this.restoreView();
      this.syncOpener();

      if (state.drawerOpen && this.panel) {
        this.panel.setAttribute('data-open', '');
        this.panel.setAttribute('role', 'dialog');
        this.panel.setAttribute('aria-modal', 'true');
        this.querySelector('[data-facets-open]')?.setAttribute('aria-expanded', 'true');
        this.setInert(true);
      }

      if (!state.hadFocus) return;
      const target = state.activeId ? root.querySelector(`#${CSS.escape(state.activeId)}`) : null;
      if (target) {
        target.focus({ preventScroll: true });
        if (state.selection && typeof target.setSelectionRange === 'function') {
          try {
            target.setSelectionRange(...state.selection);
          } catch (error) {
            /* number inputs don't support selection ranges */
          }
        }
      } else if (state.drawerOpen && this.panel) {
        (this.panel.querySelector('.facets-panel__close') || this.panel).focus({ preventScroll: true });
      } else {
        // The focused control (a removed pill, a chip) no longer exists: land on the results.
        this.querySelector('[data-results]')?.focus({ preventScroll: true });
      }
    }

    announce(text) {
      if (!this.live || !text) return;
      this.live.textContent = '';
      window.setTimeout(() => {
        this.live.textContent = text.trim();
      }, 100);
    }
  }

  customElements.define('grail-collection', GrailCollection);
})();
